import { useNavigate } from 'react-router-dom';
import { Button, secondaryTextClassName, Panel } from '@monecolier/ui';
import type { FinancialOverview } from '@monecolier/api-client';

interface FinancialOverviewPanelProps {
  overview: FinancialOverview;
  // UX-74 : ce composant n'a qu'un seul appelant réel (Home.tsx, sous le
  // <h2> "Bonjour" de SchoolAdminHome) — exposé quand même comme prop
  // plutôt qu'un h3 en dur, même patron que ClassHeadcountPanel/
  // RecentEnrollmentRequestsPanel (deux vrais appelants), pour rester
  // correct si un futur second consommateur apparaît dans un contexte
  // différent.
  titleAs?: 'h2' | 'h3' | 'h4';
}

/**
 * UX-44 — "Aperçu financier" (Accueil). Réutilise TEL QUEL `GET
 * /dashboard/financial-overview` (DSH-01, déjà consommé par
 * `FinancialDashboard.tsx`) — `recoveryRate` (fraction 0-1, DÉJÀ calculé
 * côté serveur, jamais recalculé ici) fournit directement le % demandé par
 * le ticket, aucune extension backend nécessaire (vérifié Étape 0).
 *
 * ⚠️ **Décision de portée MVP, documentée comme demandé** : contrairement à
 * `FinancialDashboard.tsx`, ce panneau n'affiche PAS les montants bruts
 * (`totalCollected`/`totalInvoiced` en devise) — les formater exigerait un
 * appel réseau supplémentaire (`GET /schools/:id#currency`, absent du
 * bootstrap de `Home.tsx`) pour un gain marginal sur un simple aperçu ; le
 * pourcentage + la barre suffisent au coup d'œil demandé par le ticket
 * ("% encaissé/attendu"), le détail complet reste à un clic ("Voir la
 * Comptabilité").
 *
 * UX-67 : "Voir la Comptabilité" était un lien texte (`linkClassName`) en
 * action principale du panneau — converti en `<Button>` (voir
 * docs/history/UX.md, section "UX-67").
 */
export function FinancialOverviewPanel({ overview, titleAs }: FinancialOverviewPanelProps) {
  const navigate = useNavigate();
  const percent = overview.recoveryRate === null ? null : Math.round(overview.recoveryRate * 100);

  return (
    <Panel title="Aperçu financier" titleAs={titleAs}>
      {percent === null ? (
        <p className={secondaryTextClassName} data-testid="financial-overview-neutral">
          {overview.schoolYearLabel
            ? `Aucune facture émise pour ${overview.schoolYearLabel} pour le moment.`
            : "Aucune année scolaire active — configurez-en une pour suivre l'encaissement."}
        </p>
      ) : (
        <>
          <p className="text-3xl font-semibold text-gray-900" data-testid="financial-overview-percent">
            {percent}%
          </p>
          <p className={secondaryTextClassName}>
            Encaissé sur attendu{overview.schoolYearLabel ? ` — ${overview.schoolYearLabel}` : ''}.
          </p>
          <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-gray-100">
            <div
              className="h-full rounded-full bg-brand-600"
              style={{ width: `${Math.min(Math.max(percent, 0), 100)}%` }}
              data-testid="financial-overview-bar"
            />
          </div>
        </>
      )}
      <Button
        type="button"
        variant="primary"
        size="small"
        className="mt-3 w-full"
        onClick={() => navigate('/financial-dashboard')}
        data-testid="financial-overview-see-all"
      >
        Voir la Comptabilité
      </Button>
    </Panel>
  );
}
